// src/services/clientManager.js
import { db } from './firebase';
import { 
  collection, 
  query, 
  where, 
  orderBy,
  getDocs
} from 'firebase/firestore';
import { 
  getSirvoyClientByEmail, 
  getSirvoyClientStats, 
  getSirvoyClientHistoryByEmail 
} from './mongoSirvoyArchives';

// ✅ Import des fonctions MongoDB pour clients Valetier
import * as mongoClients from './mongoValetierClients';

// ✅ Re-export de normalizeString depuis mongoValetierClients
export const normalizeString = mongoClients.normalizeString;

// Génère un numéro de client unique
async function generateClientNumber() {
  const maxNumber = await mongoClients.getMaxClientNumber();
  return String(maxNumber + 1).padStart(6, '0');
}

// Génère un numéro de membre du club unique
async function generateClubMemberNumber() {
  const maxNumber = await mongoClients.getMaxClubMemberNumber();
  return String(maxNumber + 1).padStart(4, '0');
}

// ✅ Re-export de findExistingClient depuis mongoValetierClients
export const findExistingClient = mongoClients.findExistingClient;

// Crée un nouveau client (sirvoyHistory déjà intégré si fourni dans clientData)
export async function createClient(clientData) {
  // ✅ Ne plus chercher dans Sirvoy ici, c'est fait dans findOrCreateClient()
  // Le sirvoyHistory est passé directement dans clientData si nécessaire
  
  return await mongoClients.createClient(clientData);
}

// ✅ Re-export de updateClientFromReservation depuis mongoValetierClients
export const updateClientFromReservation = mongoClients.updateClientFromReservation;

// ✅ Re-export de updateClientStats depuis mongoValetierClients
export const updateClientStats = mongoClients.updateClientStats;

// 🆕 NOUVELLE FONCTION : Recalculer les statistiques d'un client
export async function recalculateClientStats(clientId) {
  try {
    // ✅ Récupérer le client depuis MongoDB
    const client = await mongoClients.getClientById(clientId);
    
    if (!client) {
      console.error('Client introuvable:', clientId);
      return;
    }
    
    // Récupérer toutes les réservations du client depuis Firebase
    const reservationsRef = collection(db, 'reservations');
    const q = query(reservationsRef, where('clientId', '==', clientId));
    const snapshot = await getDocs(q);
    
    let totalReservations = 0;
    let totalCancellations = 0;
    let cancellationCount = 0;
    let returnCount = 0;
    let firstReservationDate = null;
    let lastReservationDate = null;
    
    snapshot.forEach(doc => {
      const reservation = doc.data();
      
      if (reservation.status === 'cancelled') {
        totalCancellations++;
        cancellationCount++;
      } else {
        totalReservations++;
        returnCount++;
        
        const checkInDate = reservation.checkIn?.toDate ? reservation.checkIn.toDate() : new Date(reservation.checkIn);
        
        if (!firstReservationDate || checkInDate < firstReservationDate) {
          firstReservationDate = checkInDate;
        }
        
        if (!lastReservationDate || checkInDate > lastReservationDate) {
          lastReservationDate = checkInDate;
        }
      }
    });
    
    // ✅ Mettre à jour le client dans MongoDB
    await mongoClients.updateClient(clientId, {
      totalReservations,
      totalCancellations,
      cancellationCount,
      returnCount,
      firstReservationDate: firstReservationDate ? firstReservationDate.toISOString() : null,
      lastReservationDate: lastReservationDate ? lastReservationDate.toISOString() : null,
      isReturningClient: totalReservations > 1
    });
    
    console.log(`✅ Stats recalculées pour client ${clientId}:`, {
      totalReservations,
      totalCancellations,
      cancellationCount,
      returnCount
    });
    
  } catch (error) {
    console.error('Erreur recalcul stats client:', error);
    throw error;
  }
}

// ============================================
// 🆕 GESTION ASYNC : findOrCreateClient()
// ============================================

const clientCreationLocks = new Map();

export async function findOrCreateClient(clientData) {
  const email = clientData.email?.toLowerCase().trim();
  
  // Si un client avec cet email est déjà en cours de création, on attend
  if (email && clientCreationLocks.has(email)) {
    console.log('⏳ Création client en cours, attente...');
    return await clientCreationLocks.get(email);
  }
  
  const creationPromise = (async () => {
    try {
      const existingClient = await findExistingClient(clientData);
      
      // ============================================
      // CAS 1 : Client VALETIER trouvé
      // ============================================
      if (existingClient && !existingClient.isSirvoyArchive) {
        console.log('✅ Client VALETIER existant:', existingClient.id);
        return existingClient;
      }
      
      // ============================================
      // CAS 2 : Client SIRVOY trouvé → MIGRATION AUTO
      // ============================================
      if (existingClient && existingClient.isSirvoyArchive) {
        console.log('📦 Client SIRVOY trouvé, migration vers VALETIER...');
        
        // Récupérer les stats Sirvoy complètes
        const sirvoyHistory = {
          sirvoyClientNumber: existingClient.clientNumber || null,
          sirvoyClientId: existingClient._id || existingClient.id,
          totalReservations: existingClient.totalReservations || 0,
          totalCancellations: existingClient.totalCancellations || 0,
          totalSpent: existingClient.totalSpent || 0,
          firstVisit: existingClient.firstVisit || null,
          lastVisit: existingClient.lastVisit || null,
          years: existingClient.years || [],
          migratedAt: new Date().toISOString()
        };
        
        // Créer dans VALETIER avec les données Sirvoy + nouvelles données
        const migratedClient = await createClient({
          firstName: clientData.firstName || existingClient.firstName || '',
          name: clientData.name || existingClient.name || existingClient.lastName || '',
          email: clientData.email || existingClient.email || '',
          phone: clientData.phone || existingClient.phone || '',
          address: clientData.address || existingClient.address || '',
          city: clientData.city || existingClient.city || '',
          postalCode: clientData.postalCode || existingClient.postalCode || '',
          country: clientData.country || existingClient.country || '',
          company: clientData.company || existingClient.company || '',
          sirvoyHistory,
          isReturningClient: sirvoyHistory.totalReservations > 0
        });
        
        console.log(`✅ Client migré de SIRVOY vers VALETIER: ${migratedClient.id}`);
        return migratedClient;
      }
      
      // ============================================
      // CAS 3 : NOUVEAU CLIENT
      // ============================================
      console.log('🆕 Création nouveau client...');
      const newClient = await createClient(clientData);
      return newClient;
    } finally {
      if (email) {
        clientCreationLocks.delete(email);
      }
    }
  })();
  
  if (email) {
    clientCreationLocks.set(email, creationPromise);
  }
  
  return await creationPromise;
}



// Récupère toutes les réservations d'un client
export async function getClientReservations(clientId) {
  const reservationsRef = collection(db, 'reservations');
  const q = query(
    reservationsRef,
    where('clientId', '==', clientId),
    orderBy('checkIn', 'desc')
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

// ✅ Re-export de getAllClients depuis mongoValetierClients
export const getAllClients = mongoClients.getAllClients;

// ✅ Re-export de activateClubMembership depuis mongoValetierClients
export const activateClubMembership = mongoClients.activateClubMembership;

// ✅ Re-export de deactivateClubMembership depuis mongoValetierClients
export const deactivateClubMembership = mongoClients.deactivateClubMembership;

// ✅ Re-export de updateClientTags depuis mongoValetierClients
export const updateClientTags = mongoClients.updateClientTags;

// ✅ Re-export de updateClientPreferences depuis mongoValetierClients
export const updateClientPreferences = mongoClients.updateClientPreferences;

// ============================================
// 🆕 NOUVELLES FONCTIONS POUR CHANGEMENT CLIENT
// ============================================

// ✅ Re-export de searchClients depuis mongoValetierClients
export const searchClients = mongoClients.searchClients;

/**
 * Détache une réservation d'un client
 */
export const unlinkReservationFromClient = async (reservationId, oldClientId) => {
  try {
    if (!oldClientId) return;
    
    // Recalculer les stats du client après détachement
    await recalculateClientStats(oldClientId);
  } catch (error) {
    console.error('Erreur détachement réservation:', error);
    throw error;
  }
};

/**
 * Lie une réservation à un client
 */
export const linkReservationToClient = async (reservationId, newClientId, reservationData) => {
  try {
    // ✅ Import nécessaire pour updateDoc Firebase (seulement pour les réservations)
    const { doc, updateDoc, Timestamp } = await import('firebase/firestore');
    
    // 1. Mettre à jour la réservation (dans Firebase)
    await updateDoc(doc(db, 'reservations', reservationId), {
      clientId: newClientId,
      modifiedAt: Timestamp.now()
    });
    
    // 2. Recalculer les stats du nouveau client
    await recalculateClientStats(newClientId);
    
    // 3. ✅ Mettre à jour les infos de contact depuis la réservation (MongoDB)
    const client = await mongoClients.getClientById(newClientId);
    
    if (client) {
      await mongoClients.updateClient(newClientId, {
        phone: reservationData.client.phone || client.phone,
        email: reservationData.client.email || client.email,
        address: reservationData.client.address || client.address,
        city: reservationData.client.city || client.city,
        postalCode: reservationData.client.postalCode || client.postalCode,
        country: reservationData.client.country || client.country
      });
    }
  } catch (error) {
    console.error('Erreur liaison réservation:', error);
    throw error;
  }
};

/**
 * Crée un nouveau client à partir d'une réservation
 */
export const createClientFromReservation = async (reservation) => {
  try {
    // ✅ Import nécessaire pour updateDoc Firebase (seulement pour les réservations)
    const { doc, updateDoc, Timestamp } = await import('firebase/firestore');
    
    const clientData = {
      firstName: reservation.client.firstName || '',
      name: reservation.client.name || '',
      email: reservation.client.email || '',
      phone: reservation.client.phone || '',
      address: reservation.client.address || '',
      city: reservation.client.city || '',
      postalCode: reservation.client.postalCode || '',
      country: reservation.client.country || '',
      company: reservation.client.company || ''
    };
    
    // ✅ Créer le nouveau client dans MongoDB
    const newClient = await mongoClients.createClient(clientData);
    
    // Mettre à jour la réservation avec le nouveau clientId (dans Firebase)
    await updateDoc(doc(db, 'reservations', reservation.id), {
      clientId: newClient.id,
      modifiedAt: Timestamp.now()
    });
    
    // Mettre à jour les stats du client
    await updateClientStats(newClient.id, reservation);
    
    return newClient.id;
  } catch (error) {
    console.error('Erreur création client:', error);
    throw error;
  }
};